import { classes } from 'html-classes';
import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { MultibuyPriceLevel } from '~/api/Catalog';
import Icon from '~/components/Icon/Icon';
import {
  convertPenceToPounds,
  formatPriceWithCurrency,
} from '~/utils/formaters';
import { getApplicableMultibuyLevel } from '~/utils/multibuy';

import styles from './MultibuyLevels.module.scss';

export const MultibuyLevels = React.memo(
  ({
    levels,
    allLevels = levels,
    count,
    basePrice,
  }: {
    levels: MultibuyPriceLevel[];
    allLevels?: MultibuyPriceLevel[];
    count: number;
    basePrice: number;
  }) => {
    const activeLevel = useMemo(
      () => getApplicableMultibuyLevel(allLevels, count),
      [allLevels, count],
    );

    return (
      <>
        {levels.map((level) => (
          <Level
            key={level.id}
            level={level}
            basePrice={basePrice}
            isActive={activeLevel?.id === level.id}
          />
        ))}
      </>
    );
  },
);
MultibuyLevels.displayName = 'MultibuyLevels';

interface LevelProps
  extends Omit<React.ComponentPropsWithoutRef<'div'>, 'children'> {
  level: MultibuyPriceLevel;
  basePrice: number;
  isActive: boolean;
}

const Level = React.memo(
  ({ level, basePrice, isActive, className, ...props }: LevelProps) => {
    const { t } = useTranslation();

    const discountValue = formatPriceWithCurrency(
      convertPenceToPounds((basePrice - level.unitPrice) * level.quantity),
    );

    return (
      <div
        {...props}
        key={level.id}
        className={classes([isActive && styles.levelActive, className])}
      >
        <div className={styles.levelContent}>
          <div className={styles.levelText}>
            <span className={styles.levelOffer}>
              {t('phrases:multibuyOfferNoCartValue', {
                count: level.quantity,
                value: discountValue,
              })}
              &nbsp;
              <span className={styles.levelPrice}>
                {formatPriceWithCurrency(convertPenceToPounds(level.unitPrice))}
                &nbsp;/ {t('item')}
              </span>
            </span>
          </div>
          {isActive && (
            <div className={styles.appliedBadge}>
              <span className={styles.appliedText}>
                {t('phrases:multibuyOfferApplied')}
              </span>
              <Icon
                type="check-round-3.1"
                size={16}
                className={styles.checkIcon}
              />
            </div>
          )}
        </div>
      </div>
    );
  },
);
Level.displayName = 'Level';
